const messageToKeyMap = {
  // Global / infrastructure
  "Invalid token. Please log in again!": "errors.auth.invalid_token",
  "Your token has expired. Please log in again!": "errors.auth.expired_token",
  "Something went wrong": "errors.generic",

  // Auth
  "Password and passwordConfirm must be the same": "errors.auth.password_mismatch",
  "Please provide email and password!": "errors.auth.missing_credentials",
  "Incorrect email or password": "errors.auth.invalid_credentials",
  unauthorized: "errors.auth.unauthorized",
  "You are not logged in! Please log in to get access": "errors.auth.not_logged_in",
  "The user belonging to this token does no longer exist": "errors.auth.user_not_found_for_token",
  "User recently changed password! Please log in again": "errors.auth.password_recently_changed",
  "You do not have permission to perform this action.": "errors.auth.forbidden",
  "You do not have permission to perform this action": "errors.auth.forbidden",
  "School ID is required": "errors.school.id_required",
  "You do not have permission to access this school": "errors.school.forbidden",

  // User
  "This route is not for updating passwords.": "errors.user.route_not_for_password",
  "User already exists": "errors.user.already_exists",
  "User ID is required": "errors.user.id_required",
  "Email and token are required": "errors.user.email_and_token_required",
  "Invalid or expired verification token": "errors.user.verification_invalid",
  "Verification token has expired. Please request a new one.": "errors.user.verification_expired",
  "User not found": "errors.user.not_found",
  "Email already verified": "errors.user.email_already_verified",
  "Failed to send verification email": "errors.user.verification_email_failed",
  "Email, password, and password confirmation are required":
    "errors.user.missing_email_or_password_or_confirm",
  "User not found or not verified": "errors.user.not_found_or_not_verified",
  "Password already set for this user": "errors.user.password_already_set",
  "User is using social media login. Cannot set password.": "errors.user.social_login_cannot_set_password",
  "Password is required": "errors.user.password_required",
  "Email and Google token are required": "errors.user.email_and_google_token_required",
  "Invalid Google token": "errors.user.invalid_google_token",
  "Google account email does not match": "errors.user.google_email_mismatch",
  "User already has a password. Cannot link Google account.": "errors.user.google_link_password_exists",
  "Email is required for testing": "errors.user.test_email_required",

  // School
  "Not school found with that ID": "errors.school.not_found_with_id",
  "School not found": "errors.school.not_found",
  "Teacher not found": "errors.school.teacher_not_found",
  "Teacher already added": "errors.school.teacher_already_added",
  "Assistant not found": "errors.school.assistant_not_found",
  "Assistant already added": "errors.school.assistant_already_added",

  // Subscription
  "Please provide dateExpired": "errors.subscription.date_expired_required",
  "Not Subscription found with that ID": "errors.subscription.not_found_with_id",
  "Status is required": "errors.subscription.status_required",
  "Subscription not found": "errors.subscription.not_found",
  "Subscription does not belong to this school": "errors.subscription.not_belong_to_school",
  "Subscription status is already set to '${status}'": "errors.subscription.status_already_set",
  "You already have an active subscription at this school": "errors.subscription.already_active",
  "You already have a subscription request pending confirmation from staff":
    "errors.subscription.pending_request_exists",

  // Schedule / QR / attendance
  "Schedule not found": "errors.schedule.not_found",
  "Subscription ID is required": "errors.schedule.subscription_id_required",
  "Subscription not found": "errors.schedule.subscription_not_found",
  "You cannot take attendance for yourself. Only owners, admins or assistants can take attendance.":
    "errors.schedule.cannot_take_own_attendance",
  "Only owners, admins or assistants from the same school can take attendance":
    "errors.schedule.only_same_school_staff",
  "Schedule is missing school information.": "errors.schedule.missing_school_info",
  "You can only take attendance for students in schools you own":
    "errors.schedule.only_own_school_students",
  "Unable to verify school association. User missing school information.":
    "errors.schedule.unable_verify_school_association",
  "You can only take attendance for students in your school": "errors.schedule.only_your_school_students",
  "Subscription does not belong to the same school as the schedule":
    "errors.schedule.subscription_not_same_school",
  "Subscription has expired": "errors.schedule.subscription_expired",
  "You have already taken attendance for this class": "errors.schedule.attendance_already_taken",
  "Subscription does not include this dance style": "errors.schedule.subscription_not_include_dance_style",
  "Failed to update schedule attendance": "errors.schedule.update_attendance_failed",
  "Teacher ID is required": "errors.schedule.teacher_id_required",
  "Teacher is not assigned to this schedule": "errors.schedule.teacher_not_assigned",
  "Teacher has already taken attendance for this class": "errors.schedule.teacher_already_taken_attendance",
  "Student not found": "errors.schedule.student_not_found",
  "QR token is required": "errors.schedule.qr_token_required",
  "QR code has expired": "errors.schedule.qr_code_expired",
  "Schedule does not belong to this school": "errors.schedule.not_belong_to_school",
  "Student has already taken attendance for this class":
    "errors.schedule.student_already_taken_attendance",
};

// Returns { key, params } or null
function detectPattern(message) {
  if (!message) return null;

  if (message.startsWith("Invalid input data.")) {
    return { key: "errors.validation.invalid_input", params: {} };
  }

  if (message.startsWith("Invalid ")) {
    return { key: "errors.db.invalid_field", params: {} };
  }

  if (message.startsWith("Duplicate field value:")) {
    return { key: "errors.db.duplicate_field", params: {} };
  }

  if (message.startsWith("Can't find ") && message.endsWith(" on this server!")) {
    return { key: "errors.generic_not_found", params: {} };
  }

  if (message.startsWith("Subscription status is already set to")) {
    return { key: "errors.subscription.status_already_set", params: {} };
  }

  if (message.startsWith("Password validation failed:")) {
    return { key: "errors.user.password_validation_failed", params: {} };
  }

  if (message.startsWith("Subscription is not active. Current status:")) {
    return { key: "errors.subscription.not_active", params: {} };
  }

  // QR dynamic messages
  const qrTimePrefix = "QR code can only be generated until 10 minutes before class ends. Class ends at ";
  if (message.startsWith(qrTimePrefix)) {
    return {
      key: "errors.schedule.qr_time_window_ended",
      params: { time: message.slice(qrTimePrefix.length) },
    };
  }

  const qrDayIsPrefix = "QR code can only be generated on the day of the class. Class is on ";
  if (message.startsWith(qrDayIsPrefix)) {
    return {
      key: "errors.schedule.qr_not_day_yet",
      params: { date: message.slice(qrDayIsPrefix.length) },
    };
  }

  const qrDayWasPrefix = "QR code can only be generated on the day of the class. Class was on ";
  if (message.startsWith(qrDayWasPrefix)) {
    return {
      key: "errors.schedule.qr_not_day_past",
      params: { date: message.slice(qrDayWasPrefix.length) },
    };
  }

  return null;
}

export function mapApiError(error) {
  // Network / no response
  if (!error || !error.response) {
    return {
      status: null,
      rawMessage: error && error.message ? error.message : "Network error",
      code: "network",
      i18nKey: "errors.network",
      i18nParams: {},
      originalError: error,
    };
  }

  const status = error.response.status;
  const data = error.response.data || {};
  const rawMessage = data.message || error.message || "Error";

  const directKey = messageToKeyMap[rawMessage];
  const pattern = directKey ? null : detectPattern(rawMessage);

  const i18nKey = directKey || (pattern && pattern.key) || "errors.generic_with_message";
  const i18nParams = directKey
    ? {}
    : pattern
    ? pattern.params
    : { message: rawMessage };

  return {
    status,
    rawMessage,
    code: directKey || (pattern && pattern.key) || "generic",
    i18nKey,
    i18nParams,
    originalError: error,
  };
}

